import { useScrollContent } from '@exam-management/common/hooks';
import {
  useCreateInDevelopmentQuestionMutation,
  GetQuestionCreationTaskByIdDocument,
  QuestionGroupType,
  GetTaskQuestionGroupByIdDocument,
} from '@exam-management/graphql';
import { useQuestionCreationTaskDetails } from '@exam-management/question-creation-task-details';
import { useItemSetQuestionDetails } from '@exam-management/question-creation-task-question-management';
import { parseContentForApi } from '@exam-management/rich-text-editor';
import { Button, Flex } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { parseQuestionFormDataForApi } from '../single-question-creation-form';

import { DEFAULT_FORM_VALUES } from './constants';
import type { CreateItemSetSubQuestionFormInput } from './item-set-sub-question-creation.schema';

export function ItemSetSubQuestionCreationFormActions() {
  const { showSnackbar } = useSnackbar();
  const { itemSet } = useItemSetQuestionDetails();
  const { task } = useQuestionCreationTaskDetails();
  const { scrollTo } = useScrollContent();
  const formContext = useFormContext<CreateItemSetSubQuestionFormInput>();
  const itemSetCreation = task?.metadata?.requirement?.itemSetCreation?.find(
    (creation) => creation.questionGroupId === itemSet.id,
  );

  const { formState } = formContext;
  const { isSubmitSuccessful } = formState;

  const isLoading = false;
  const [createInDevelopmentQuestion] = useCreateInDevelopmentQuestionMutation({
    onError: () => {
      showSnackbar({
        message: 'Unable to Save Question',
        variant: 'error',
      });
    },
  });

  const createSingleQuestion = async (data: CreateItemSetSubQuestionFormInput) => {
    const qBankStructureLevelItemId = data.location.SUB_TOPIC?.value as string;
    const qBankStructureId = task.metadata?.requirement.qBankStructure?.id;

    if (!qBankStructureLevelItemId || !qBankStructureId)
      throw Error('Invalid qbs id and level item id');

    const payload = parseQuestionFormDataForApi(
      data,
      parseContentForApi,
      QuestionGroupType.ItemSet,
      itemSet.id,
    );

    const result = await createInDevelopmentQuestion({
      variables: {
        input: {
          qBankStructureLevelItemId,
          refQBankStructureId: qBankStructureId,
          data: payload,
        },
        taskId: task.id,
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetQuestionCreationTaskByIdDocument,
          variables: {
            getTaskByIdInput: {
              taskId: task.id,
            },
          },
        },
        {
          query: GetTaskQuestionGroupByIdDocument,
          variables: {
            questionGroupId: itemSet.id,
            taskId: task.id,
          },
        },
      ],
      onCompleted: () => {
        scrollTo({ x: 0, y: 0 });
        showSnackbar({
          message: 'Question has been saved',
          variant: 'success',
        });
      },
    });

    if (result.errors) throw result.errors;
  };

  const handleShowRequiredFieldsError = () => {
    showSnackbar({
      message: 'Please complete all required fields',
      variant: 'error',
    });
  };

  const handleSaveAndCreateNew = async () => {
    await formContext.handleSubmit(async (data) => {
      await createSingleQuestion(data);
    }, handleShowRequiredFieldsError)();
  };

  useEffect(() => {
    if (isSubmitSuccessful) {
      formContext.reset({
        ...DEFAULT_FORM_VALUES,
        location: formContext.getValues('location'),
      });
    }
  }, [isSubmitSuccessful, formContext]);

  const shouldAllowSaveAndCreateNew = itemSetCreation
    ? itemSetCreation?.questionCreationCount + 1 < itemSetCreation?.size
    : false;

  return (
    <Flex sx={{ gap: 4 }}>
      <Button
        type='submit'
        disabled={formContext.formState.isSubmitting || !formContext.formState.isDirty}
        // NOTE: This is a workaround to show loading status for only one button
        loading={formContext.formState.isSubmitting && !isLoading}
      >
        Save
      </Button>
      {shouldAllowSaveAndCreateNew ? (
        <Button
          type='button'
          color='secondary'
          variant='outlined'
          disabled={formContext.formState.isSubmitting || !formContext.formState.isDirty}
          loading={isLoading}
          onClick={handleSaveAndCreateNew}
        >
          Save & Create New
        </Button>
      ) : null}
    </Flex>
  );
}
